import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import { parseCatalogSearch, type CatalogSearch } from '@features/product-catalog';
import { ProductCatalogPage } from '@pages/product-catalog';
import type { RouterContext } from '@shared/config/router-context';
import { canAccessAnalyticsGear } from '@shared/lib';

export const Route = createFileRoute('/$locale/_authenticated/gear-erp/products')({
  validateSearch: parseCatalogSearch,
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessAnalyticsGear(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirects are thrown response objects.
      throw redirect({ to: '/$locale/unauthorized', params: { locale: params.locale } });
    }
  },
  component: CatalogRoute,
});

function CatalogRoute() {
  const { locale } = Route.useParams();
  const query = Route.useSearch();
  const navigate = Route.useNavigate();
  const auth = useAuth();
  const ability = useAbility();
  const canRead = canAccessAnalyticsGear(ability);
  const onQueryChange = useCallback(
    (search: CatalogSearch) => {
      void navigate({ search, replace: true });
    },
    [navigate]
  );
  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated && !canRead) {
      void navigate({ to: '/$locale/unauthorized', params: { locale }, replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, canRead, locale, navigate]);
  if (auth.isLoading || !auth.isAuthenticated || !canRead) return null;
  const viewerAccessScope = auth.user
    ? `${auth.user.id}:${JSON.stringify(auth.permissions ?? [])}`
    : '';
  return (
    <ProductCatalogPage
      locale={locale}
      query={query}
      onQueryChange={onQueryChange}
      viewerAccessScope={viewerAccessScope}
    />
  );
}
